// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { Consent, type ConsentOutput } from '@imprivata-cloud/consent';
import { landingPageDataTanstackCacheKey } from '@portal/containers/users/hooks/useAuthenticators';
import { useBiometricConsentPorts } from '@selfservice/hooks/useBiometricConsentPorts';
import {
  CONSENT_DOCUMENT_UNAVAILABLE_MESSAGE,
  DONE_AUTO_CONTINUE_MS,
} from '@selfservice/utils/helpers';
import { useSelfServiceData } from '@selfservice/utils/useSelfServiceData';
import { useQueryClient } from '@tanstack/react-query';
import { Flex, Spin } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { RootRoutes } from '@/index';

import '../../styles.less';

/**
 * Biometric re-consent (ME-11391). Reuses the shipped `@imprivata-cloud/consent`
 * module rather than reimplementing consent:
 *
 * - `forceConsent` skips the "is consent needed" short-circuit, so a user whose
 *   grant is already recorded is still walked through residence selection and
 *   the agreement.
 * - `showIntro` renders the module's re-consent lead-in explaining why consent
 *   is being collected again.
 * - `showDone` renders the module's acknowledgement screen before returning, with
 *   `doneAutoContinueMs` driving its Continue button's progress fill.
 * - `showConfirmationModal` puts a confirm in front of the agreement screens'
 *   decline, because declining here deletes an already-enrolled face.
 */
const UpdateBioAgreement = () => {
  const { t } = useTranslation();
  const { userInfo } = useSession();
  const { emitError } = useErrorContext();
  const navigateKeepSearch = useNavigateKeepSearch();
  const queryClient = useQueryClient();
  const location = useLocation();

  const { data, isLoading } = useSelfServiceData();
  const { input, ports } = useBiometricConsentPorts(userInfo, data?.residenceLocations);

  // Return to wherever the flow was launched from, falling back to the
  // authenticators list.
  const returnToOrigin = useCallback(() => {
    navigateKeepSearch(location.state?.from || RootRoutes.SELF_SERVICE);
  }, [location.state?.from, navigateKeepSearch]);

  const onComplete = useCallback(
    (output: ConsentOutput) => {
      // The grant changed, so the cached landing-page payload (authenticators
      // and consent state) is stale.
      if (output.kind !== 'cancelled') {
        queryClient.invalidateQueries({ queryKey: [landingPageDataTanstackCacheKey], exact: true });
      }
      returnToOrigin();
    },
    [queryClient, returnToOrigin],
  );

  const onConsentUnavailable = useCallback(() => {
    emitError(AppError.fromMessage(CONSENT_DOCUMENT_UNAVAILABLE_MESSAGE));
    returnToOrigin();
  }, [emitError, returnToOrigin]);

  if (isLoading) {
    return (
      <div className="enroll-content">
        <Flex align="center" justify="center">
          <Spin />
        </Flex>
      </div>
    );
  }

  return (
    <div className="enroll-content">
      <Consent
        input={input}
        ports={ports}
        translate={(key, fallback, params) => t(key, { defaultValue: fallback, ...params })}
        onAbort={onConsentUnavailable}
        onComplete={onComplete}
        onCancel={returnToOrigin}
        onError={message => emitError(AppError.fromMessage(message))}
        forceConsent
        showDone
        showConfirmationModal
        doneAutoContinueMs={DONE_AUTO_CONTINUE_MS}
      />
    </div>
  );
};

export default UpdateBioAgreement;
